// Wspoldzielony mechanizm odswiezania JWT przez refresh_token.
// Uzywany przez `axios.ts` (glowne API) oraz `wiseAiApi.ts` (asystent AI).
// Single-flight: rownolegle requesty z 401 czekaja na ten sam refresh, zeby nie zalewac
// backendu wieloma refreshami przy salwie zapytan z asystenta (tool calls + add-message).

import axios, { AxiosError, AxiosInstance } from 'axios';
import { store, reduxActions } from 'store';

const {
  REACT_APP_API_BASE_URL,
  REACT_APP_API_BASE_PATH,
  REACT_APP_OPEN_PROFILE_CLIENT_ID,
  REACT_APP_OPEN_PROFILE_CLIENT_SECRET
} = process.env;

const JWT_REFRESH_TOKEN = 'JWT_REFRESH_TOKEN';
const JWT_SESSION_ID = 'JWT_SESSION_ID';

let refreshingPromise: Promise<string> | null = null;

const startRefresh = (): Promise<string> => {
  const refreshToken = localStorage.getItem(JWT_REFRESH_TOKEN);
  if (!refreshToken || refreshToken === 'undefined') {
    store.dispatch(reduxActions.signOut());
    return Promise.reject(new Error('Brak refresh tokena'));
  }

  return axios
    .post(`${REACT_APP_API_BASE_URL}${REACT_APP_API_BASE_PATH}/auth/refresh-token`, {
      refreshToken,
      clientId: REACT_APP_OPEN_PROFILE_CLIENT_ID || '',
      clientSecret: REACT_APP_OPEN_PROFILE_CLIENT_SECRET || ''
    })
    .then((response) => {
      const newAccessToken = response?.data?.access_token;
      const newRefreshToken = response?.data?.refresh_token;
      const newSessionId = response?.data?.session_id;

      if (!newAccessToken) {
        throw new Error(response?.data?.hint || 'Refresh token bez access_token');
      }

      // Defensywa: gdy backend zwroci puste session_id (cache miss w PostRefreshTokenService
      // lub starsza wersja backendu), zachowaj poprzedni sessionId zamiast wyzerowac Redux.
      // Bez tego asystent AI traci sessionId po pierwszym refresh tokena.
      const sessionIdToStore = newSessionId || localStorage.getItem(JWT_SESSION_ID) || '';

      store.dispatch(
        reduxActions.refreshTokens({
          token: newAccessToken,
          refreshToken: newRefreshToken,
          sessionId: sessionIdToStore
        })
      );

      return newAccessToken as string;
    })
    .catch((err) => {
      localStorage.removeItem(JWT_REFRESH_TOKEN);
      store.dispatch(reduxActions.signOut());
      throw err;
    });
};

/**
 * Single-flight refresh tokenow JWT.
 *
 * Gdy wiele rownoleglych requestow dostanie 401 (czesto w asystencie AI - add-message
 * + tool calls), pierwszy uruchamia refresh, kolejne czekaja na ten sam promise.
 * Po sukcesie kazdy ponawia oryginalny request z nowym tokenem na axiosInstance.
 */
export const refreshTokensAndRetry = async (
  axiosInstance: AxiosInstance,
  error: AxiosError
): Promise<unknown> => {
  if (!refreshingPromise) {
    refreshingPromise = startRefresh().finally(() => {
      refreshingPromise = null;
    });
  }

  const newAccessToken = await refreshingPromise;
  const config = error.config;
  if (!config) {
    throw error;
  }
  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${newAccessToken}`
  };
  return axiosInstance(config);
};
